import { TLemmaResult } from '../rest/words/wordsRest';

export const TEXT_BUCKET_LABELS = ['Frequent', 'Several times', 'Once'];
export const LANGUAGE_BUCKET_LABELS = ['Basic', 'Common', 'Rare'];

export type TFrequencyTab = 'text' | 'language';

export type TWordLemmaGroup = {
	lemma: string;
	forms: { word: string; count: number }[];
	// occurrences shown on the chip: non-foreign ones, or foreign ones for a foreign group
	total: number;
	textBucket: number;
	languageBucket: number;
	foreign: boolean;
};

const SEPARATOR = /[^\p{L}\p{N}']+/u;
const FOREIGN_CAPTION_SHARE = 0.5;

export function tokenizeText(text: string): string[] {
	return text.toLowerCase().split(SEPARATOR)
		.map(w => w.replace(/^'+|'+$/g, ''))
		.filter(w => w.length > 1);
}

export function tokenizeCaptions(texts: string[]): string[][] {
	return texts.map(tokenizeText);
}

export function collectWords(tokenized: string[][]): string[] {
	return Array.from(new Set(tokenized.flat()));
}

// Judged only by tokens whose lemma is already loaded — a caption with none loaded yet isn't foreign.
function isForeignCaption(tokens: string[], lemmas: Record<string, TLemmaResult>): boolean {
	const known = tokens.filter(t => lemmas[t]);
	if (!known.length) return false;
	return known.filter(t => lemmas[t].foreign).length / known.length > FOREIGN_CAPTION_SHARE;
}

function textBucketOf(count: number): number {
	if (count >= 5) return 0;
	if (count >= 2) return 1;
	return 2;
}

// Groups word forms by lemma. An occurrence is foreign if the word is, or its whole caption is mostly
// foreign; a group is foreign when most of its occurrences are.
export function groupWordsByLemma(tokenized: string[][], lemmas: Record<string, TLemmaResult>): TWordLemmaGroup[] {
	const forms = new Map<string, { count: number, foreignCount: number }>();
	tokenized.forEach(tokens => {
		const captionForeign = isForeignCaption(tokens, lemmas);
		tokens.forEach(word => {
			const form = forms.get(word) || { count: 0, foreignCount: 0 };
			form.count++;
			if (captionForeign || lemmas[word]?.foreign) form.foreignCount++;
			forms.set(word, form);
		});
	});

	type TRawGroup = Omit<TWordLemmaGroup, 'forms'> & { forms: { word: string; count: number; foreignCount: number }[]; all: number; foreignAll: number };
	const groups = new Map<string, TRawGroup>();
	forms.forEach(({ count, foreignCount }, word) => {
		const info = lemmas[word];
		const lemma = info?.lemma || word;
		if (!groups.has(lemma)) {
			groups.set(lemma, { lemma, forms: [], total: 0, textBucket: 0, languageBucket: LANGUAGE_BUCKET_LABELS.length - 1, foreign: false, all: 0, foreignAll: 0 });
		}
		const group = groups.get(lemma) as TRawGroup;
		group.forms.push({ word, count, foreignCount });
		group.all += count;
		group.foreignAll += foreignCount;
		// not loaded yet counts as rare until the band arrives
		if (info) group.languageBucket = Math.min(group.languageBucket, info.band - 1);
	});

	return Array.from(groups.values()).map(({ all, foreignAll, ...group }) => {
		const foreign = foreignAll > all - foreignAll;
		const total = foreign ? foreignAll : all - foreignAll;
		// forms carry the count the chip displays (non-foreign, or foreign for a foreign group)
		const shown = group.forms.map(f => ({ word: f.word, count: foreign ? f.foreignCount : f.count - f.foreignCount }));
		shown.sort((a, b) => b.count - a.count);
		return { ...group, forms: shown, foreign, total, textBucket: textBucketOf(total) };
	}).sort((a, b) => b.total - a.total);
}
